// ── Draw Forecast ─────────────────────────────────────────────────────────
//
// One question: WHEN DOES THIS BOOK DRAW, and how much should a controller
// have ready on a given day or week. Answered from funded history — which
// weekdays, which part of the month, which calendar dates actually carry the
// volume.
//
// THERE IS DELIBERATELY NO FORWARD SCHEDULE HERE. Draws carry an
// expectedfunddate and an expecteddrawamount, and an earlier version of this
// page charted them as a dated pipeline. That was removed: the fields are not
// maintained in practice, so the chart was drawing a schedule nobody keeps and
// giving it the authority of a calendar. A forecast built on the history of
// what actually funded is honest about being an estimate; a calendar of dates
// nobody enters is not. If those fields ever become reliable, the pipeline
// view is worth rebuilding — but as an addition, not a replacement.
//
// THE AVERAGE THAT MATTERS IS PER DAY, NOT PER DRAW. Averaging draw amounts
// answers "how big is a typical Wednesday draw" — a day with eleven small
// draws looks quieter than a day with one large one, which is backwards for
// anyone deciding how much cash to have ready. Every average here divides by
// the NUMBER OF SUCH DAYS IN THE WINDOW, including the days nothing was drawn,
// because a Wednesday with no draws is evidence about Wednesdays.
//
// Everything comes from getDraws as it already exists. No API change.

import { useState, useEffect, useMemo } from "react";
import { B, RAMP_BLUE, INK_MUTED, INK_PRIMARY, SURFACE_MUTED } from "../../../shared/theme";
import { Card, Spinner, ErrorMsg, FilterSelect, Btn, TabBtn } from "../../../shared/ui";
import { fmt, exportCSV } from "../../../shared/format";
import { useApp } from "../../../shared/appContext";
import { useChartTheme, ChartThemeToggle, chartCardStyle } from "../../../shared/chartTheme";

const DOW  = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAYS = [1, 2, 3, 4, 5];               // draws fund on business days
const WOM_LABELS = ["1st–7th", "8th–14th", "15th–21st", "22nd–28th", "29th+"];

const num = v => parseFloat(v || 0) || 0;
const short = v => {
  const a = Math.abs(v), t = x => String(Math.abs(x) >= 100 ? Math.round(x) : Math.round(x * 10) / 10);
  return a >= 1e9 ? "$" + t(v/1e9) + "B" : a >= 1e6 ? "$" + t(v/1e6) + "M"
       : a >= 1e3 ? "$" + t(v/1e3) + "K" : "$" + Math.round(v);
};

// SQL dates arrive as "YYYY-MM-DD..." strings. Parsing them with new Date(str)
// reads them as UTC midnight and then renders them in the browser's zone, which
// moves a Monday draw to Sunday for anyone west of Greenwich. Every date on
// this page is built from the string parts as a LOCAL date instead.
const parseDate = d => {
  if (!d) return null;
  const s = String(d).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, day] = s.split("-").map(Number);
  const dt = new Date(y, m - 1, day);
  // Date ROLLS OVER impossible days rather than failing: new Date(2026, 1, 31)
  // is 3 March, not an error. Round-tripping the parts is the only way to tell
  // "31 February" apart from a date that exists, and a hand-typed date box can
  // produce exactly that.
  if (isNaN(dt) || dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== day)
    return null;
  return dt;
};
const addDays = (dt, n) => { const d = new Date(dt); d.setDate(d.getDate() + n); return d; };
const startOfToday = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
// Week of month by calendar day, not by "nth Wednesday". Borrowers draw against
// payroll and month-end payables, which land on dates.
const weekOfMonth = dt => Math.min(5, Math.ceil(dt.getDate() / 7));

const isFunded = d => {
  const f = d.fundeddate;
  return !!(f && String(f).trim() !== "");
};

// A loan that is neither accruing nor on non-accrual is resolved — paid off,
// charged off, terminated, or never funded. It will not draw again, so it does
// not belong in a funding forecast.
//
// loanstatus is NOT a column: getLoans derives it from legal events, funded
// terms and terminations in a thirty-line CASE. Asking getLoans rather than
// reimplementing that is what keeps this page and the collateral analytics page
// from disagreeing about what "paid off" means.
const LIVE_STATUSES = ["Accrual", "Non Accrual"];

// Draws come off revolvers, so this page is scoped to accounts that have (or
// had) one that actually funded.
//
// DELAYED DRAW TERM LOANS ARE EXCLUDED ON PURPOSE, and this is the reason:
// a DDTL draw is normally tied to a specific equipment purchase. It is an
// event, not a rhythm. Weekday and day-of-month patterns are meaningless for
// it — the money moves when the machine is bought — so folding DDTL history
// into these averages adds volume that no pattern explains and makes every
// per-day figure less useful for the revolver activity a controller can
// actually plan around. Do not "fix" this by widening the match.
//
// MATCHED EXACTLY, NOT BY SUBSTRING. The facility types in this database
// include BOTH "Revolving Line of Credit" and "Non Revolving Line of Credit" —
// a test for the word "revolving" quietly pulls in the facility that is
// defined by not being one. Case and inner whitespace are normalised so a
// typed-in variant still matches; nothing else is.
const REVOLVER = "revolving line of credit";
const isRevolver = ft =>
  String(ft || "").trim().toLowerCase().replace(/\s+/g, " ") === REVOLVER;

// "Funding in Progress" is the one loan status that unambiguously means the
// facility never funded. Everything else — accruing, non-accrual, paid off,
// charged off, terminated — describes a loan that advanced money at some point,
// which is what "had" a revolver means.
const NEVER_FUNDED = "Funding in Progress";

const WINDOWS = [
  { id: "6",  label: "6 months",  months: 6  },
  { id: "12", label: "12 months", months: 12 },
  { id: "24", label: "24 months", months: 24 },
  { id: "all", label: "All history", months: 0 },
];
// ── heat grid ─────────────────────────────────────────────────────────────
// Dark theme: the same four steps as glowing cyan of rising strength, each
// with the ink that reads on it — the light ramp's pale tints are lighter
// than the navy card and would read backwards.
const RAMP_DARK = [
  { fill:"rgba(56,214,245,0.16)", ink:"#ffffff" },
  { fill:"rgba(56,214,245,0.34)", ink:"#ffffff" },
  { fill:"rgba(56,214,245,0.62)", ink:"#062030" },
  { fill:"#38d6f5",               ink:"#062030" },
];
const HeatGrid = ({ cells, mode, maxVal, T }) => {
  const ramp = T.dark ? RAMP_DARK : RAMP_BLUE;
  const step = v => {
    if (!(v > 0)) return null;
    const i = Math.min(ramp.length - 1, Math.floor((v / maxVal) * ramp.length));
    return ramp[i];
  };
  const th = { fontSize:11, fontWeight:700, color:T.muted,
               textTransform:"uppercase", letterSpacing:"0.05em", padding:"4px 6px", textAlign:"center" };
  return (
    <div style={{ overflowX:"auto" }}>
      <table style={{ borderCollapse:"separate", borderSpacing:4, width:"100%", minWidth:460 }}>
        <thead><tr>
          <th style={{ ...th, textAlign:"left" }} />
          {WEEKDAYS.map(w => <th key={w} style={th}>{DOW[w]}</th>)}
          <th style={{ ...th, color:T.accent }}>Row</th>
        </tr></thead>
        <tbody>
          {[1,2,3,4,5].map(wom => {
            const row = WEEKDAYS.map(w => cells[wom + "|" + w] || { total:0, days:0, draws:0 });
            const rowTot = row.reduce((s,c) => s + c.total, 0);
            const rowDays = row.reduce((s,c) => s + c.days, 0);
            return (
              <tr key={wom}>
                <td style={{ ...th, textAlign:"left", whiteSpace:"nowrap" }}>{WOM_LABELS[wom-1]}</td>
                {WEEKDAYS.map((w, i) => {
                  const c = row[i];
                  const v = mode === "avg" ? (c.days ? c.total / c.days : 0) : c.total;
                  const s = step(v);
                  return (
                    <td key={w} title={
                          `${WOM_LABELS[wom-1]}, ${DOW[w]}\n` +
                          `${c.draws} draw${c.draws===1?"":"s"} over ${c.days} such day${c.days===1?"":"s"}\n` +
                          `Total ${fmt(c.total)}\n` +
                          `Average per day ${fmt(c.days ? c.total/c.days : 0)}\n` +
                          `Average per draw ${fmt(c.draws ? c.total/c.draws : 0)}`}
                        style={{ textAlign:"center", padding:"10px 6px", borderRadius:6, minWidth:66,
                                 background: s ? s.fill : T.panel,
                                 color: s ? s.ink : T.muted,
                                 border: s ? "none" : "1px solid "+T.grid,
                                 boxShadow: s && T.dark && s === ramp[ramp.length-1]
                                   ? "0 0 12px rgba(56,214,245,0.45)" : "none",
                                 fontSize:12, fontWeight:600, fontVariantNumeric:"tabular-nums" }}>
                      {v > 0 ? short(v) : "—"}
                      <div style={{ fontSize:9, fontWeight:500, opacity:0.75, marginTop:2 }}>
                        {c.draws || 0} draw{c.draws===1?"":"s"}
                      </div>
                    </td>
                  );
                })}
                <td style={{ textAlign:"center", padding:"10px 6px", fontSize:12, fontWeight:700,
                             color:T.accent, fontVariantNumeric:"tabular-nums" }}>
                  {short(mode === "avg" ? (rowDays ? rowTot/rowDays : 0) : rowTot)}
                </td>
              </tr>
            );
          })}
          <tr>
            <td style={{ ...th, textAlign:"left", color:T.accent }}>Column</td>
            {WEEKDAYS.map(w => {
              let t = 0, d = 0;
              [1,2,3,4,5].forEach(wom => { const c = cells[wom+"|"+w]; if (c) { t += c.total; d += c.days; } });
              return <td key={w} style={{ textAlign:"center", padding:"10px 6px", fontSize:12, fontWeight:700,
                                          color:T.accent, fontVariantNumeric:"tabular-nums" }}>
                       {short(mode === "avg" ? (d ? t/d : 0) : t)}
                     </td>;
            })}
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export const DrawForecastPage = () => {
  const { api } = useApp();
  const T = useChartTheme();
  const [draws, setDraws] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [company, setCompany] = useState("");
  const [win, setWin] = useState("12");
  const [mode, setMode] = useState("avg");
  // Default ON. This page exists to tell a controller how much is funding and
  // when; a resolved loan will never fund again, so leaving it in is wrong by
  // default rather than merely noisy.
  const [activeOnly, setActiveOnly] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("getDraws"), api.get("getLoans")])
      .then(([d, l]) => {
        setDraws(Array.isArray(d) ? d : []);
        setLoans(Array.isArray(l) ? l : []);
        setLoading(false);
      })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const loanStatus = useMemo(() => {
    const m = {};
    loans.forEach(x => { m[String(x.id)] = x.loanstatus; });
    return m;
  }, [loans]);

  // A draw whose loan getLoans did not return has an unknown status, not a
  // resolved one. It stays in. Silently dropping money from a cash forecast is
  // a worse failure than showing a stale loan, so unknowns are counted and
  // called out rather than hidden.
  const liveOf = d => {
    const s = loanStatus[String(d.loanid)];
    return s === undefined ? null : LIVE_STATUSES.includes(s);
  };

  // ── revolver scope ──────────────────────────────────────────────────────
  // The dropdown offers accounts that have, or had, a revolver that funded.
  // "Had" is deliberate: a paid-off revolver's draw history is exactly the
  // evidence this page is built on, so past tense counts. The test is on the
  // LOAN — a funded revolver — not on the account having a revolver somewhere
  // and being funded somewhere else.
  //
  // The whole page is scoped to these accounts, not just the picker. Offering
  // a dropdown of revolver accounts while "All companies" quietly averaged in
  // term loans would make the aggregate disagree with every single-company
  // view under it, and the filter would be invisible in the one view people
  // read most.
  const revolverAccounts = useMemo(() => {
    const names = new Set();
    loans.forEach(l => {
      if (!isRevolver(l.facilitytype)) return;
      if (l.loanstatus === NEVER_FUNDED) return;
      if (l.companyname) names.add(l.companyname);
    });
    return names;
  }, [loans]);

  // An eligible account that has never had a single draw is noise in a picker
  // meant for reading draw history, so the list is the intersection.
  const companies = useMemo(() => {
    const withDraws = new Set(draws.map(d => d.companyname).filter(Boolean));
    return [...revolverAccounts].filter(c => withDraws.has(c)).sort()
      .map(c => ({ value:c, label:c }));
  }, [draws, revolverAccounts]);

  const inScope = useMemo(() => draws.filter(d => revolverAccounts.has(d.companyname)),
                          [draws, revolverAccounts]);

  // If the selected company falls out of scope — the data reloaded, or a
  // facility changed — drop back to All rather than showing an empty page with
  // a company name still in the box.
  useEffect(() => {
    if (company && !revolverAccounts.has(company)) setCompany("");
  }, [company, revolverAccounts]);

  const excludedAccounts = useMemo(() => {
    const all = new Set(draws.map(d => d.companyname).filter(Boolean));
    return [...all].filter(c => !revolverAccounts.has(c)).sort();
  }, [draws, revolverAccounts]);

  const scoped = useMemo(() => company ? inScope.filter(d => d.companyname === company) : inScope,
                         [inScope, company]);

  // How much the toggle is actually hiding. Counted in LOANS, not draws — a
  // controller thinks in "which credits are still live", and one paid-off
  // revolver can account for a hundred historical draws.
  const resolvedInfo = useMemo(() => {
    const dead = new Set(), unknownLoans = new Set();
    let amount = 0;
    scoped.forEach(d => {
      const live = liveOf(d);
      if (live === null) { unknownLoans.add(String(d.loanid)); return; }
      if (!live) { dead.add(String(d.loanid)); amount += num(d.fundingamount); }
    });
    return { n: dead.size, amount, unknown: unknownLoans.size };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoped, loanStatus]);

  const rows = useMemo(() => activeOnly ? scoped.filter(d => liveOf(d) !== false) : scoped,
                       // eslint-disable-next-line react-hooks/exhaustive-deps
                       [scoped, activeOnly, loanStatus]);

  // ── history: funded draws, and the days they could have landed on ───────
  const history = useMemo(() => {
    const today = startOfToday();
    const months = (WINDOWS.find(w => w.id === win) || WINDOWS[1]).months;
    const from = months ? addDays(today, -Math.round(months * 30.44)) : null;

    const funded = rows.filter(isFunded).map(d => ({ ...d, dt: parseDate(d.fundeddate) }))
                       .filter(d => d.dt && (!from || d.dt >= from) && d.dt <= today);

    // The denominator. Every business day in the window counts, including the
    // ones nothing funded on — that is the whole point of a per-day average.
    let start = from;
    if (!start) {
      const earliest = funded.reduce((m, d) => (!m || d.dt < m ? d.dt : m), null);
      start = earliest || today;
    }
    const cells = {}, byDom = {}, byDow = {};
    for (let d = new Date(start); d <= today; d = addDays(d, 1)) {
      const w = d.getDay();
      if (!WEEKDAYS.includes(w)) continue;
      const k = weekOfMonth(d) + "|" + w;
      (cells[k] = cells[k] || { total:0, days:0, draws:0 }).days++;
      (byDow[w] = byDow[w] || { total:0, days:0, draws:0 }).days++;
      const dom = d.getDate();
      (byDom[dom] = byDom[dom] || { total:0, days:0, draws:0 }).days++;
    }
    funded.forEach(d => {
      const w = d.dt.getDay();
      const amt = num(d.fundingamount);
      if (!WEEKDAYS.includes(w)) {
        // A draw funded on a weekend is real money; it just has no business-day
        // cell. Counted in the totals below, absent from the grid, and called
        // out so the two never look like they disagree.
        return;
      }
      const k = weekOfMonth(d.dt) + "|" + w;
      const c = cells[k]; if (c) { c.total += amt; c.draws++; }
      const dw = byDow[w]; if (dw) { dw.total += amt; dw.draws++; }
      const dm = byDom[d.dt.getDate()]; if (dm) { dm.total += amt; dm.draws++; }
    });

    const weekend = rows.filter(isFunded).map(d => ({ ...d, dt: parseDate(d.fundeddate) }))
      .filter(d => d.dt && (!from || d.dt >= from) && d.dt <= today && !WEEKDAYS.includes(d.dt.getDay()));

    const total = funded.reduce((s, d) => s + num(d.fundingamount), 0);
    const bizDays = Object.values(cells).reduce((s, c) => s + c.days, 0);
    return { funded, cells, byDom, byDow, total, bizDays, weekend,
             perDay: bizDays ? total / bizDays : 0, start, today };
  }, [rows, win]);

  // The one number a controller asks for out loud: which day is the big one.
  const heaviest = useMemo(() => {
    const rank = obj => Object.entries(obj)
      .map(([k, c]) => ({ k, w: Number(k), perDay: c.days ? c.total / c.days : 0, ...c }))
      .filter(x => x.perDay > 0)
      .sort((a, b) => b.perDay - a.perDay)[0] || null;
    return { dow: rank(history.byDow), dom: rank(history.byDom) };
  }, [history.byDow, history.byDom]);

  const maxCell = useMemo(() => Math.max(1, ...Object.values(history.cells).map(c =>
    mode === "avg" ? (c.days ? c.total/c.days : 0) : c.total)), [history.cells, mode]);

  const domMax = useMemo(() => Math.max(1, ...Object.values(history.byDom).map(c =>
    mode === "avg" ? (c.days ? c.total/c.days : 0) : c.total)), [history.byDom, mode]);

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const cardTitle = { fontSize:11, fontWeight:700, color:B.cyanDark, textTransform:"uppercase",
                      letterSpacing:"0.06em", marginBottom:10, paddingBottom:6,
                      borderBottom:"1.5px solid "+B.cyanLight };
  // The two chart cards follow the Dark / Light chart switch.
  const chartTitle = { ...cardTitle, color:T.title, borderBottom:"1.5px solid "+T.titleRule };
  const stat = (label, value, sub, accent) => (
    <Card style={{ padding:"12px 16px", flex:"1 1 170px", minWidth:150,
                   borderLeft:"3px solid " + (accent || B.cyan) }}>
      <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em",
                    color:INK_MUTED, marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:18, fontWeight:600 }}>{value}</div>
      <div style={{ fontSize:11, color:INK_MUTED, marginTop:2 }}>{sub}</div>
    </Card>
  );

  return (
    <>
      <Card style={{ padding:14, marginBottom:14 }}>
        <div style={{ display:"flex", flexWrap:"wrap", gap:12, alignItems:"flex-end" }}>
          <FilterSelect label="Company" value={company} onChange={setCompany}
                        options={companies}
                        allLabel={`All revolvers (${companies.length})`} />
          <FilterSelect label="History" value={win} onChange={setWin}
                        options={WINDOWS.map(w => ({ value:w.id, label:w.label }))} allLabel={null} />

          {/* Greyed rather than hidden when there is nothing to filter, so its
              absence never reads as a missing feature. */}
          <label style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:12,
                          paddingBottom:2, whiteSpace:"nowrap",
                          opacity: resolvedInfo.n > 0 ? 1 : 0.45,
                          cursor: resolvedInfo.n > 0 ? "pointer" : "default" }}
                 title={resolvedInfo.n === 0
                      ? "Every loan that drew in this window is Accrual or Non Accrual — nothing to filter."
                      : `Hides loans that are not Accrual or Non Accrual — paid off, charged off, `
                        + `terminated, or never funded. They will not draw again, so their history is `
                        + `not evidence about what is coming. ${resolvedInfo.n} loan`
                        + `${resolvedInfo.n === 1 ? "" : "s"} (${fmt(resolvedInfo.amount)} funded in `
                        + `this window) affected.`}>
            <input type="checkbox" checked={activeOnly}
                   disabled={resolvedInfo.n === 0}
                   onChange={e => setActiveOnly(e.target.checked)} />
            Active loans only{resolvedInfo.n > 0 ? ` (${resolvedInfo.n} hidden)` : ""}
          </label>

          <div style={{ marginLeft:"auto" }}>
            <Btn onClick={() => exportCSV(
                   [1,2,3,4,5].flatMap(wom => WEEKDAYS.map(w => {
                     const c = history.cells[wom + "|" + w] || { total:0, days:0, draws:0 };
                     return {
                       week_of_month: WOM_LABELS[wom-1],
                       weekday: DOW[w],
                       days_in_window: c.days,
                       draws: c.draws,
                       total_funded: c.total.toFixed(2),
                       avg_per_day: (c.days ? c.total / c.days : 0).toFixed(2),
                       avg_per_draw: (c.draws ? c.total / c.draws : 0).toFixed(2),
                     };
                   })),
                   ["week_of_month","weekday","days_in_window","draws","total_funded",
                    "avg_per_day","avg_per_draw"],
                   "draw-pattern.csv")}
                 disabled={!history.funded.length}>Export pattern</Btn>
          </div>
        </div>
      </Card>

      <div style={{ display:"flex", flexWrap:"wrap", gap:12, marginBottom:14 }}>
        {stat("Typical business day", fmt(history.perDay),
              `${history.bizDays} business day${history.bizDays===1?"":"s"} in window`)}
        {stat("Typical week", fmt(history.perDay * 5), "5 business days")}
        {stat("Heaviest weekday", heaviest.dow ? DOW[heaviest.dow.w] : "—",
              heaviest.dow ? fmt(heaviest.dow.perDay) + " on a typical one" : "no history",
              B.amber)}
        {stat("Funded in window", fmt(history.total),
              `${history.funded.length} draw${history.funded.length===1?"":"s"}`)}
      </div>

      {/* The toggle changes every number above and below it, including the
          historical averages — a loan that has been paid off will not draw
          again, so its past draws are not evidence about what is coming. Say
          which basis is on screen rather than letting a checkbox change the
          answer quietly. */}
      {/* The scope is a filter, and an unannounced filter is how two people
          read the same screen and get different numbers. */}
      <div style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"9px 13px",
                    marginBottom:14, borderRadius:8, background:SURFACE_MUTED,
                    borderLeft:"3px solid "+B.cyan, fontSize:12, lineHeight:1.6,
                    color:INK_PRIMARY }}>
        <span style={{ fontSize:13, lineHeight:1.3 }} aria-hidden="true">ⓘ</span>
        <span>
          <strong>Revolving lines of credit only.</strong> {companies.length} account
          {companies.length === 1 ? "" : "s"} that have or had a funded revolver.
          {excludedAccounts.length > 0 && (
            <> {excludedAccounts.length} account{excludedAccounts.length === 1 ? "" : "s"} with
              draw history {excludedAccounts.length === 1 ? "is" : "are"} excluded — term loans,
              delayed draw term loans, non-revolving lines, and facilities that never funded
              {" "}(<span title={excludedAccounts.join(", ")} style={{ textDecoration:"underline",
                       textDecorationStyle:"dotted", cursor:"help" }}>hover for the list</span>).</>
          )}
        </span>
      </div>

      {(resolvedInfo.n > 0 || resolvedInfo.unknown > 0) && (
        <div style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"9px 13px",
                      marginBottom:14, borderRadius:8,
                      background: activeOnly ? SURFACE_MUTED : B.amberLight,
                      borderLeft:"3px solid " + (activeOnly ? B.cyan : B.amber),
                      fontSize:12, lineHeight:1.6, color:INK_PRIMARY }}>
          <span style={{ fontSize:13, lineHeight:1.3 }} aria-hidden="true">ⓘ</span>
          <span>
            {activeOnly
              ? <><strong>Active loans only.</strong> {resolvedInfo.n} resolved
                  loan{resolvedInfo.n === 1 ? "" : "s"} and {fmt(resolvedInfo.amount)} of
                  {" "}{resolvedInfo.n === 1 ? "its" : "their"} funded history
                  {" "}{resolvedInfo.n === 1 ? "is" : "are"} excluded. A paid-off loan will not
                  draw again, so its past draws are not evidence about what is coming.</>
              : <><strong>All loans, including resolved ones.</strong> {resolvedInfo.n} paid-off,
                  charged-off or terminated loan{resolvedInfo.n === 1 ? "" : "s"} and
                  {" "}{fmt(resolvedInfo.amount)} of history {resolvedInfo.n === 1 ? "is" : "are"}
                  {" "}included. Those credits cannot draw again, so every average on this page is
                  a blend of the book you have and one you do not.</>}
            {resolvedInfo.unknown > 0 && (
              <> {resolvedInfo.unknown} loan{resolvedInfo.unknown === 1 ? "" : "s"} could not be
                matched to a status and {resolvedInfo.unknown === 1 ? "is" : "are"} shown either
                way rather than dropped.</>
            )}
          </span>
        </div>
      )}

      {/* ── 2. when draws land ────────────────────────────────────────────── */}
      <Card style={{ padding:16, marginBottom:14, ...chartCardStyle(T) }}>
        <div style={{ display:"flex", flexWrap:"wrap", gap:10, alignItems:"center",
                      justifyContent:"space-between", marginBottom:12 }}>
          <div style={{ ...chartTitle, marginBottom:0, paddingBottom:0, border:"none" }}>
            When draws land — funded history
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <ChartThemeToggle />
            <TabBtn id="avg"   current={mode} label="Average per day" onClick={setMode} />
            <TabBtn id="total" current={mode} label="Total"           onClick={setMode} />
          </div>
        </div>

        {/* The improvement over a weekday bar chart with a week-of-month
            slicer: crossing the two shows the interaction. "Wednesdays are
            heavy" and "month-end is heavy" can both be true while the actual
            spike is one cell. A slicer makes you click five times and
            remember; a grid shows it. */}
        <HeatGrid cells={history.cells} mode={mode} maxVal={maxCell} T={T} />

        <div style={{ marginTop:8, fontSize:11, color:T.muted, lineHeight:1.6 }}>
          {mode === "avg"
            ? <>Each cell is total funded ÷ <strong>the number of such days in the window</strong>,
                counting days with no draws. That is the cash-planning number: a Wednesday with
                eleven small draws should read heavier than one with a single large draw, and an
                average per draw reads it the other way round.</>
            : <>Each cell is everything funded on those days over the window. Totals favour cells
                with more occurrences — switch to average per day to compare like with like.</>}
          {history.weekend.length > 0 && (
            <> {history.weekend.length} draw{history.weekend.length===1?"":"s"} funded on a weekend
              ({fmt(history.weekend.reduce((s,d)=>s+num(d.fundingamount),0))}) sit outside this grid.</>
          )}
        </div>
      </Card>

      {/* ── 3. day of month ───────────────────────────────────────────────── */}
      <Card style={{ padding:16, marginBottom:14, ...chartCardStyle(T) }}>
        <div style={{ ...chartTitle, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          Day of month
          <ChartThemeToggle />
        </div>
        <div style={{ display:"flex", alignItems:"flex-end", gap:2, height:120, marginBottom:6 }}>
          {Array.from({ length:31 }, (_, i) => i+1).map(dom => {
            const c = history.byDom[dom] || { total:0, days:0, draws:0 };
            const v = mode === "avg" ? (c.days ? c.total/c.days : 0) : c.total;
            const h = domMax ? (v / domMax) * 100 : 0;
            const heavy = v >= domMax * 0.7;
            return (
              <div key={dom} style={{ flex:1, display:"flex", flexDirection:"column",
                                      alignItems:"center", justifyContent:"flex-end", height:"100%" }}
                   title={`Day ${dom}\n${c.draws} draw${c.draws===1?"":"s"} over ${c.days} occurrence${c.days===1?"":"s"}\nTotal ${fmt(c.total)}\nAverage per day ${fmt(c.days?c.total/c.days:0)}`}>
                {/* Shaded, rounded columns: the same treatment as the
                    Portfolio Balance chart, in either theme. */}
                <div style={{ width:"78%", height:Math.max(h, v > 0 ? 2 : 0)+"%", borderRadius:"3px 3px 0 0",
                              background: heavy
                                ? `linear-gradient(180deg, ${T.warmTop}, ${T.warmBot})`
                                : `linear-gradient(180deg, ${T.accentTop}, ${T.accentBot})`,
                              boxShadow: T.dark && v > 0
                                ? (heavy ? "0 0 8px rgba(255,192,115,0.35)" : "0 0 8px rgba(56,214,245,0.30)")
                                : "none" }} />
              </div>
            );
          })}
        </div>
        <div style={{ display:"flex", gap:2 }}>
          {Array.from({ length:31 }, (_, i) => i+1).map(dom => (
            <div key={dom} style={{ flex:1, textAlign:"center", fontSize:8,
                                    color:T.muted }}>
              {dom % 5 === 0 || dom === 1 ? dom : ""}
            </div>
          ))}
        </div>
        <div style={{ marginTop:8, fontSize:11, color:T.muted, lineHeight:1.6 }}>
          Weekday and week-of-month both smooth over the calendar. If this book draws around payroll
          or a month-end payables run, the spike is on a <em>date</em> and only shows here. Gold marks
          days at or above 70% of the busiest.
        </div>
      </Card>

    </>
  );
};
